import React from 'react';
import { useNavigate } from 'react-router-dom';

// Animated first-screen for the native app when signed out — replaces the
// marketing website home. Branded, calm, and gets the user to sign up / in fast.

const STARS = Array.from({ length: 14 }, (_, i) => ({
  id: i,
  left: `${(i * 37) % 100}%`,
  top: `${(i * 53) % 70}%`,
  size: 6 + ((i * 7) % 10),
  delay: `${(i % 5) * 0.4}s`,
  dur: `${2.4 + (i % 4) * 0.6}s`,
}));

const HIGHLIGHTS = [
  { icon: '⭐', text: 'Your child is the hero of every story' },
  { icon: '🎙️', text: 'Read aloud in your own voice' },
  { icon: '🌍', text: 'In 11 languages, ready in seconds' },
];

const NativeWelcome = () => {
  const navigate = useNavigate();

  return (
    <div
      style={{
        // #root already reserves the safe-area insets, so fill exactly what's
        // left — with overflow:hidden a 100vh here would clip the bottom CTA.
        minHeight: 'calc(100vh - env(safe-area-inset-top) - env(safe-area-inset-bottom))',
        background: 'linear-gradient(160deg,#1e1048 0%,#2d1b69 55%,#4a2a9e 100%)',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        padding: '28px 26px',
        textAlign: 'center', position: 'relative', overflow: 'hidden',
      }}
    >
      <style>{`
        @keyframes nw-rise { from { opacity: 0; transform: translateY(22px); } to { opacity: 1; transform: none; } }
        @keyframes nw-moon { 0%,100% { transform: translateY(0) rotate(-6deg); } 50% { transform: translateY(-10px) rotate(6deg); } }
        @keyframes nw-twinkle { 0%,100% { opacity: .15; transform: scale(.8); } 50% { opacity: .9; transform: scale(1.15); } }
        .nw-in { animation: nw-rise .7s cubic-bezier(.2,.7,.2,1) both; }
      `}</style>

      {/* Twinkling stars */}
      <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
        {STARS.map((s) => (
          <span key={s.id} style={{
            position: 'absolute', left: s.left, top: s.top, width: s.size, height: s.size,
            borderRadius: '50%', background: 'radial-gradient(circle,#fff,rgba(255,255,255,0))',
            animation: `nw-twinkle ${s.dur} ${s.delay} ease-in-out infinite`,
          }} />
        ))}
      </div>

      {/* Moon */}
      <div className="nw-in" style={{ fontSize: 76, lineHeight: 1, animation: 'nw-moon 5s ease-in-out infinite', marginBottom: 18 }}>🌙</div>

      <h1 className="nw-in" style={{
        fontFamily: "'Fredoka One',cursive", color: '#fff', fontSize: 34, margin: '0 0 10px',
        animationDelay: '.06s', textShadow: '0 2px 20px rgba(0,0,0,.25)',
      }}>
        Dreamy Bedtime
      </h1>

      <p className="nw-in" style={{ color: 'rgba(255,255,255,.82)', fontSize: 16, lineHeight: 1.5, maxWidth: 320, margin: '0 0 26px', animationDelay: '.12s' }}>
        Personalised bedtime stories that make your child the hero — ready in seconds.
      </p>

      {/* Highlights */}
      <div className="nw-in" style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 34, animationDelay: '.18s', width: '100%', maxWidth: 320 }}>
        {HIGHLIGHTS.map((h, i) => (
          <div key={i} style={{
            display: 'flex', alignItems: 'center', gap: 12, textAlign: 'left',
            background: 'rgba(255,255,255,.08)', border: '1px solid rgba(255,255,255,.12)',
            borderRadius: 16, padding: '12px 16px', backdropFilter: 'blur(4px)',
          }}>
            <span style={{ fontSize: 22, flexShrink: 0 }}>{h.icon}</span>
            <span style={{ color: '#fff', fontSize: 14, fontWeight: 500 }}>{h.text}</span>
          </div>
        ))}
      </div>

      {/* CTAs */}
      <div className="nw-in" style={{ width: '100%', maxWidth: 320, display: 'flex', flexDirection: 'column', gap: 12, animationDelay: '.24s' }}>
        <button
          onClick={() => navigate('/register')}
          style={{
            border: 'none', borderRadius: 100, padding: '16px', fontSize: 16, fontWeight: 700,
            background: 'linear-gradient(135deg,#f9c74f,#f6a935)', color: '#2d1b69',
            boxShadow: '0 8px 22px rgba(249,199,79,.35)', cursor: 'pointer',
          }}
        >
          Get Started — it’s free
        </button>
        <button
          onClick={() => navigate('/login')}
          style={{
            border: '1px solid rgba(255,255,255,.35)', borderRadius: 100, padding: '15px', fontSize: 15, fontWeight: 600,
            background: 'rgba(255,255,255,.06)', color: '#fff', cursor: 'pointer',
          }}
        >
          I already have an account
        </button>
      </div>
    </div>
  );
};

export default NativeWelcome;
